import {
  Button,
  Callout,
  Card,
  FormGroup,
  Icon,
  InputGroup,
  Intent,
  NumericInput,
  Slider,
  Spinner,
} from '@blueprintjs/core';
import { FORM_HELPER_TEXT } from '@blueprintjs/core/lib/esm/common/classes';
import { DatasetStatus, SearchPreset } from '@retro-ui/api-contract';
import { Config } from '@retro-ui/config';
import {
  MAX_N_ATOMS_DEFAULT,
  MAX_PRICE_DEFAULT,
  MAX_PRICE_PER_GRAM_DEFAULT,
  MIN_N_PROVIDERS_DEFAULT,
  MIN_N_PROVIDERS_RANGE,
  MIN_REFERENCE_COUNT_DEFAULT,
  MIN_REFERENCE_COUNT_RANGE,
  REACTION_CONFIDENCE_THRESHOLD_DEFAULT,
  SearchLength,
  SearchReactionConfidenceThreshold,
  SearchSynthesisScale,
} from '@retro-ui/shared';
import { css, styled } from '@stitches/react';
import Big from 'big.js';
import cx from 'classnames';
import { chain, clamp, map } from 'lodash';
import { ReactNode, useMemo } from 'react';
import isEqual from 'react-fast-compare';
import { useSelector } from 'react-redux';
import { NavLink } from 'react-router-dom';

import { Select } from '@f/components/shared/select';
import { useConfig } from '@f/hooks';
import { useIsDebugModeOn } from '@f/hooks/useIsDebugModeOn';
import {
  DatasetReducerDataItem,
  StartingMaterialReducer,
} from '@f/services/starting-material/starting-material.types';

import cls from './SearchParameterForm.module.scss';
import { DatasetInput, SupplierItem } from './components/DatasetInput';
import { buildDatasetInputItems } from './services/buildDatasetInputItems';
import { buildSelectedDatasetMap } from './services/buildDatasetMap';
import { useSearchParamsValidation } from './services/useSearchParamsValidation';

export interface SearchParamsOptions {
  areDatasetsValid: boolean;
}

export interface DatasetSearchParam {
  id: string;
  selected_suppliers?: string[];
}

export type DatasetsById = Record<string, DatasetReducerDataItem>;

export type Models = Config['global']['batchSearch']['models'];

export interface SearchParameterFormProps {
  searchParams: SearchPreset;
  onChange: (updatedParams: SearchPreset) => void;
  className?: string;
  groupClassName?: string;
  footerClassName?: string;
  disabled?: boolean;
  before?: React.ReactNode;
  after?: React.ReactNode;
  footer?: React.ReactNode;
}

interface ItemData {
  value: string | number;
  label: ReactNode;
  description?: ReactNode;
}

const Root = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1.5rem',
});

export const ParamGroup = styled(Card, {
  display: 'flex',
  flexDirection: 'column',
  gap: '3rem',
  padding: '2rem',
});

export const ParamContainer = styled(FormGroup, {
  margin: 0,
});

export const paramContainerInnerStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
});

export const ErrorCallout = styled(Callout, {
  marginTop: '0.3rem',
});

const numericInputStyle = css({
  maxWidth: '4rem',
});

const numericInputStyleWithIcon = css({
  maxWidth: '8rem',
});

const MinReferencesHelperText = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '1rem',
});

const MinReferencesInputsContainer = styled('div', {
  display: 'flex',
  gap: '3rem',
});

const PrivateDatasetsContainer = styled('div', {
  display: 'flex',
  flexDirection: 'column',
  gap: '0.5rem',
  maxHeight: '15rem',
  overflowY: 'auto',
  width: 'fit-content',
  border: '1px solid $slate4',
  padding: '0.5rem',
  boxSizing: 'border-box',
});

function buildModelItems(models: Models): ItemData[] {
  return models.map(model => ({
    label: model.label,
    value: model.id,
    description: model.description,
  }));
}

function buildLengthItems(isDebug: boolean): ItemData[] {
  const forAdmin = { label: 'single step', value: 'single_step' };
  return isDebug ? [...LENGTH, forAdmin] : LENGTH;
}

function buildSynthesisScaleItems(): ItemData[] {
  return SYNTHESIS_SCALE;
}

const LENGTH = [
  {
    description: 'Sufficient for expected pathways of around 6 steps.',
    label: 'short',
    value: 'short',
  },
  {
    description:
      'Appropriate for complex compounds with 10 or more synthesis steps. Significantly increases search time.',
    label: 'regular',
    value: 'regular',
  },
];
const LENGTH_VALUES = map(buildLengthItems(true), 'value');
const LENGTH_DESCRIPTIONS = Object.fromEntries(LENGTH.map(item => [item.value, item.description]));

const SYNTHESIS_SCALE = [
  { label: 'kilogram', value: SearchSynthesisScale.KILOGRAM },
  { label: 'molar', value: SearchSynthesisScale.MOLAR },
  { label: 'millimolar', value: SearchSynthesisScale.MILLIMOLAR },
  { label: 'micromolar', value: SearchSynthesisScale.MICROMOLAR },
];
const SYNTHESIS_SCALE_VALUES = map(buildSynthesisScaleItems(), 'value');

const AVAILABILITY_TIER = [
  { description: 'All starting materials ship in 1-5 business days.', label: '1-5 days', value: 1 },
  {
    description: 'All starting materials ship in 2-10 business days.',
    label: '2-10 days',
    value: 2,
  },
  { description: 'All starting materials ship within 4 weeks.', label: '< 4 weeks', value: 3 },
  {
    description:
      'Starting materials may require synthesis and are usually shipped within 12 weeks.',
    label: '< 12 weeks',
    value: 4,
  },
  { description: 'Starting materials require custom quote (POA).', label: 'quote', value: 5 },
];
const AVAILABILITY_TIER_VALUES = map(AVAILABILITY_TIER, 'value');
const AVAILABILITY_TIER_DESCRIPTIONS = Object.fromEntries(
  AVAILABILITY_TIER.map(tier => [tier.value, tier.description])
);
const AVAILABILITY_TIER_LABELS = Object.fromEntries(
  AVAILABILITY_TIER.map(tier => [tier.value, tier.label])
);

const REACTION_CONFIDENCE_THRESHOLD: ItemData[] = [
  {
    description:
      'High threshold reduces the number of possible pathways, ensuring that only the most certain reactions are included.',
    label: 'high',
    value: SearchReactionConfidenceThreshold.HIGH,
  },
  {
    description:
      'Low threshold expands the pool of possible pathways, offering more diversity but risking the inclusion of incorrect reactions.',
    label: 'low',
    value: SearchReactionConfidenceThreshold.LOW,
  },
];
const REACTION_CONFIDENCE_THRESHOLD_VALUES = map(REACTION_CONFIDENCE_THRESHOLD, 'value');
const REACTION_CONFIDENCE_THRESHOLD_DESCRIPTIONS = Object.fromEntries(
  REACTION_CONFIDENCE_THRESHOLD.map(item => [item.value, item.description])
);

const minReferenceCountSliderSteps = [0, 1, 2, 5, 10, 20, 50, 100, 200, 500, 1000];

function getEventValue(event): boolean | string | number {
  if (!event.target) {
    return event;
  }

  switch (event.target.type) {
    case 'checkbox':
      return Boolean(event.target.checked);
    default:
      return event.target.value;
  }
}

function sanitizeSearchParams(
  presets: SearchPreset,
  datasetMap: { [extendedId: string]: DatasetReducerDataItem },
  models: Models
): SearchPreset {
  const datasets = (presets.datasets ?? []).filter(item => {
    const { id: extendedId } = item;
    const dataset = datasetMap[extendedId];
    return dataset?.status === DatasetStatus.READY;
  });

  const MODEL_VALUES = map(buildModelItems(models), 'value');

  return {
    model: MODEL_VALUES.includes(presets.model) ? presets.model : 'transformer',
    length: LENGTH_VALUES.includes(presets.length) ? presets.length : SearchLength.REGULAR,
    synthesis_scale: SYNTHESIS_SCALE_VALUES.includes(presets.synthesis_scale)
      ? presets.synthesis_scale
      : SearchSynthesisScale.MILLIMOLAR,
    datasets,
    availability_tier: AVAILABILITY_TIER_VALUES.includes(presets.availability_tier)
      ? presets.availability_tier
      : 3,
    reaction_confidence_threshold: REACTION_CONFIDENCE_THRESHOLD_VALUES.includes(
      presets.reaction_confidence_threshold
    )
      ? presets.reaction_confidence_threshold
      : REACTION_CONFIDENCE_THRESHOLD_DEFAULT,
    min_reference_count:
      presets.min_reference_count != null
        ? Number(presets.min_reference_count)
        : MIN_REFERENCE_COUNT_DEFAULT,
    excluded_smiles: presets.excluded_smiles ?? [],
    min_n_providers:
      presets.min_n_providers != null ? Number(presets.min_n_providers) : MIN_N_PROVIDERS_DEFAULT,
    max_n_atoms: presets.max_n_atoms != null ? Number(presets.max_n_atoms) : MAX_N_ATOMS_DEFAULT,
    max_price: presets.max_price != null ? Number(presets.max_price) : MAX_PRICE_DEFAULT,
    max_price_per_gram:
      presets.max_price_per_gram != null
        ? Number(presets.max_price_per_gram)
        : MAX_PRICE_PER_GRAM_DEFAULT,
  };
}

export function SearchParameterForm(props: SearchParameterFormProps) {
  const {
    searchParams: rawSearchParams,
    onChange,
    className,
    groupClassName,
    footerClassName,
    before,
    after,
    footer,
    disabled,
  } = props;

  const config = useConfig();
  const { models } = config.batchSearch;

  const fetchingDatasets = useSelector((state: any) => {
    const startingMaterial = state.startingMaterial as StartingMaterialReducer;
    return startingMaterial.dataset.find.processing > 0;
  });
  const datasetsById = useSelector((state: any) => {
    const startingMaterial = state.startingMaterial as StartingMaterialReducer;
    return startingMaterial.dataset.data;
  });

  const datasetsByExtendedId = chain(datasetsById).values().keyBy('extendedId').value();

  const isDebugModeOn = useIsDebugModeOn();

  const searchParams = useMemo(
    () => sanitizeSearchParams(rawSearchParams, datasetsByExtendedId, models),
    [rawSearchParams, datasetsByExtendedId, models]
  );

  const selectedDatasetMap = useMemo(
    () => buildSelectedDatasetMap(searchParams.datasets, datasetsByExtendedId),
    [searchParams, datasetsByExtendedId]
  );

  const { validation } = useSearchParamsValidation(searchParams);

  const onScalarPropertyChange =
    (property: string | number) =>
    (event: React.FormEvent<HTMLInputElement> | string | number): void => {
      const value = getEventValue(event);

      if (isEqual(searchParams[property], value)) {
        return;
      }

      const updatedSearchParams = {
        ...searchParams,
        [property]: value,
      };
      onChange(sanitizeSearchParams(updatedSearchParams, datasetsByExtendedId, models));
    };

  const onSelectChange =
    (property: string) =>
    (item: any): void => {
      if (isEqual(searchParams[property], item.value)) {
        return;
      }

      const updatedSearchParams = {
        ...searchParams,
        [property]: item.value,
      };
      onChange(sanitizeSearchParams(updatedSearchParams, datasetsByExtendedId, models));
    };

  const onDatasetChange =
    (datasetId: string) =>
    (nextValue: boolean | SupplierItem[]): void => {
      let updatedDatasets;

      const dataset = datasetsById[datasetId];
      const { extendedId, suppliers = {} } = dataset;

      if (!nextValue || (Array.isArray(nextValue) && nextValue.length === 0)) {
        // If the dataset value is cleared
        updatedDatasets = chain(selectedDatasetMap).omit([extendedId]).values().value();
        onScalarPropertyChange('datasets')(updatedDatasets);
        return;
      }

      if (Array.isArray(nextValue)) {
        // If the dataset value is a supplier list
        const selectedSupplierIds = nextValue.map(item => item.id);

        const selectedDataset: DatasetSearchParam = {
          id: extendedId,
          // selected_suppliers: selectedSupplierIds as string[],
        };

        if (selectedSupplierIds.length < Object.values(suppliers).length) {
          selectedDataset.selected_suppliers = selectedSupplierIds as string[];
        }

        updatedDatasets = Object.values({
          ...selectedDatasetMap,
          [extendedId]: selectedDataset,
        });
      } else {
        // If the dataset value is boolean
        updatedDatasets = Object.values({
          ...selectedDatasetMap,
          [extendedId]: {
            ...selectedDatasetMap[extendedId],
            id: extendedId,
          },
        });
      }

      onScalarPropertyChange('datasets')(updatedDatasets);
    };

  const onMinReferenceCountSliderChange = (sliderValue: number) => {
    const rounded = Math.round(sliderValue);

    const value = minReferenceCountSliderSteps[rounded];

    onScalarPropertyChange('min_reference_count')(value);
  };

  const lengthItems = buildLengthItems(isDebugModeOn);
  const modelItems = buildModelItems(models);
  const synthesisScaleItems = buildSynthesisScaleItems();
  const datasetInputItems = buildDatasetInputItems(datasetsById, selectedDatasetMap);
  const startingMaterialPublicDatasets = datasetInputItems.filter(
    item => !item.isPrivate && item.status === DatasetStatus.READY
  );
  const startingMaterialPrivateDatasets = datasetInputItems.filter(
    item => item.isPrivate && item.status === DatasetStatus.READY
  );

  const activeModel = modelItems.find(model => model.value === searchParams.model);

  const minReferenceCountSliderValue = useMemo(() => {
    const paramValue = searchParams.min_reference_count;

    const rangeMaxIdx = minReferenceCountSliderSteps.findIndex(step => step >= paramValue);

    if (rangeMaxIdx === -1) {
      return minReferenceCountSliderSteps.length - 1;
    }

    if (rangeMaxIdx === 0) {
      return 0;
    }

    const range = [
      minReferenceCountSliderSteps[rangeMaxIdx - 1],
      minReferenceCountSliderSteps[rangeMaxIdx],
    ];

    // (paramValue - range[0]) / (range[1] - range[0])
    const localValue = Big(paramValue)
      .minus(range[0])
      .div(Big(range[1]).minus(range[0]))
      .round(2)
      .toNumber();

    return rangeMaxIdx - 1 + localValue;
  }, [searchParams.min_reference_count]);

  return (
    <Root className={className}>
      {before}

      <ParamGroup className={groupClassName} data-cy="synthesis-plan-parameter-form">
        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Model"
          helperText={activeModel?.description}
          data-cy="model-select-container"
        >
          <Select
            large
            outlined
            items={modelItems}
            activeItemId={searchParams.model}
            idField="value"
            labelField="label"
            filterable={false}
            disabled={disabled}
            rightIcon="caret-down"
            onChange={onSelectChange('model')}
            allowNoSelection={false}
            data-cy="model-select"
          />
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Length"
          helperText={LENGTH_DESCRIPTIONS[searchParams.length]}
        >
          <Select
            large
            outlined
            items={lengthItems}
            activeItemId={searchParams.length}
            idField="value"
            labelField="label"
            filterable={false}
            disabled={disabled}
            rightIcon="caret-down"
            onChange={onSelectChange('length')}
            allowNoSelection={false}
            data-cy="length-select"
          />
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Synthesis scale"
          helperText="Determines the quantity for price checks. Prices per gram may vary significantly between small and large quantities. Ensure this parameter is set according to your needs."
        >
          <Select
            large
            outlined
            items={synthesisScaleItems}
            activeItemId={searchParams.synthesis_scale}
            idField="value"
            labelField="label"
            filterable={false}
            disabled={disabled}
            rightIcon="caret-down"
            onChange={onSelectChange('synthesis_scale')}
            allowNoSelection={false}
            data-cy="synthesis-scale-select"
          />
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Reaction confidence threshold"
          helperText={
            REACTION_CONFIDENCE_THRESHOLD_DESCRIPTIONS[searchParams.reaction_confidence_threshold]
          }
        >
          <Select
            large
            outlined
            items={REACTION_CONFIDENCE_THRESHOLD}
            activeItemId={searchParams.reaction_confidence_threshold}
            idField="value"
            labelField="label"
            filterable={false}
            disabled={disabled}
            rightIcon="caret-down"
            onChange={onSelectChange('reaction_confidence_threshold')}
            allowNoSelection={false}
            data-cy="reaction-confidence-threshold-select"
          />
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Minimum reference count"
          helperText={
            <MinReferencesHelperText>
              <span>
                Minimum number of references citing similar reaction center and mechanism. Higher
                values favor well-documented and commonly used reactions (potentially excluding
                those with uncommon reaction centers).
              </span>
              {searchParams.min_reference_count === 0 && (
                <Callout intent={Intent.WARNING} data-cy="min-reference-count-warning">
                  Setting this parameter to 0 may include reactions without any literature backing.
                  Additionally, when the <i>Reaction confidence threshold</i> is set to low, this
                  may lead to the inclusion of multiple incorrect reactions. We strongly recommend
                  setting this to 1 or higher.
                </Callout>
              )}
            </MinReferencesHelperText>
          }
        >
          <div data-cy="min-reference-count-slider-wrapper">
            <MinReferencesInputsContainer>
              <NumericInput
                large
                inputClassName={numericInputStyle()}
                min={MIN_REFERENCE_COUNT_RANGE[0]}
                max={MIN_REFERENCE_COUNT_RANGE[1]}
                majorStepSize={1}
                minorStepSize={1}
                disabled={disabled}
                value={searchParams.min_reference_count}
                onValueChange={value => {
                  const clampedValue = clamp(
                    value,
                    MIN_REFERENCE_COUNT_RANGE[0],
                    MIN_REFERENCE_COUNT_RANGE[1]
                  );
                  onScalarPropertyChange('min_reference_count')(clampedValue);
                }}
                data-cy="min-reference-count-input"
              />
              <Slider
                min={0}
                max={minReferenceCountSliderSteps.length - 1}
                stepSize={0.01}
                disabled={disabled}
                value={minReferenceCountSliderValue}
                labelRenderer={value =>
                  typeof minReferenceCountSliderSteps[value] === 'number'
                    ? String(minReferenceCountSliderSteps[value])
                    : String(searchParams.min_reference_count)
                }
                // labelValues={minReferenceCountSliderSteps.map((value, idx) => idx)}
                onChange={onMinReferenceCountSliderChange}
                data-cy="min-reference-count-slider"
              />
            </MinReferencesInputsContainer>
          </div>
        </ParamContainer>
      </ParamGroup>

      <ParamGroup className={groupClassName}>
        {Object.keys(datasetsById).length === 0 || fetchingDatasets ? (
          <div className={cls.loading}>
            <Spinner size={16} intent={Intent.PRIMARY} /> Loading datasets...
          </div>
        ) : (
          <>
            <ParamContainer
              contentClassName={paramContainerInnerStyle()}
              className={cls.param}
              disabled={disabled}
              label="Global starting material datasets"
              helperText="Allow only the starting materials that are available at selected suppliers."
            >
              <div className={cls.supplierAggregators} data-cy="allowed-suppliers-wrapper">
                {startingMaterialPublicDatasets.map(item => (
                  <DatasetInput
                    datasetInputItem={item}
                    onChange={onDatasetChange(item.id)}
                    key={item.id}
                  />
                ))}
              </div>
            </ParamContainer>

            <ParamContainer
              contentClassName={paramContainerInnerStyle()}
              disabled={disabled}
              label="Private starting material datasets"
              helperText=""
            >
              <div className={cls.customMaterialInventory} data-cy="allowed-suppliers-wrapper">
                {startingMaterialPrivateDatasets.length > 0 && (
                  <PrivateDatasetsContainer>
                    {startingMaterialPrivateDatasets.map(item => (
                      <DatasetInput
                        datasetInputItem={item}
                        onChange={onDatasetChange(item.id)}
                        key={item.id}
                      />
                    ))}
                  </PrivateDatasetsContainer>
                )}
                {startingMaterialPrivateDatasets.length > 5 && (
                  <div className={FORM_HELPER_TEXT}>Scroll down to view more datasets</div>
                )}

                <NavLink className={cls.newDatasetButton} to="/dashboard/starting-materials">
                  <Button
                    large
                    minimal
                    intent={Intent.PRIMARY}
                    text="New dataset"
                    icon="add"
                    data-cy="add-sm-dataset-button"
                  />
                </NavLink>
              </div>

              {validation.isDatasetSizeLimitExceeded && (
                <ErrorCallout intent={Intent.DANGER}>
                  {validation.datasetSizeLimitExceededMessage}
                </ErrorCallout>
              )}
            </ParamContainer>

            {validation.areDatasetsEmpty && (
              <Callout intent={Intent.DANGER} data-cy="callout-empty-datasets-error">
                {validation.datasetsEmptyMessage}
              </Callout>
            )}
          </>
        )}
        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Starting materials shipping time"
          helperText={
            <>
              {AVAILABILITY_TIER_DESCRIPTIONS[searchParams.availability_tier]}
              {searchParams.availability_tier < 5 && (
                <div className={cls.info}>
                  <Icon icon="info-sign" />
                  <span>
                    This restriction will still apply even if your starting material is available at
                    one of the selected suppliers.
                  </span>
                </div>
              )}
            </>
          }
        >
          <div data-cy="availability-tier-slider-wrapper">
            <Slider
              className={cls.slider}
              max={AVAILABILITY_TIER.length}
              min={1}
              disabled={disabled}
              value={searchParams.availability_tier}
              labelRenderer={value => AVAILABILITY_TIER_LABELS[value]}
              onChange={onScalarPropertyChange('availability_tier')}
              data-cy="availability-tier-slider"
            />
          </div>
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Excluded starting materials"
          helperText="Specify which compounds should not be used as starting materials for the synthesis."
        >
          <div data-cy="excluded-starting-materials-wrapper">
            <InputGroup
              large
              disabled={disabled}
              defaultValue={searchParams.excluded_smiles.join(', ')}
              onBlur={event => {
                const newValues = event.target.value
                  .split(',')
                  .map(item => item.trim())
                  .filter(item => item !== '');
                if (isEqual(searchParams.excluded_smiles, newValues)) {
                  return;
                }

                const updatedSearchParams = {
                  ...searchParams,
                  excluded_smiles: newValues,
                };
                onChange(sanitizeSearchParams(updatedSearchParams, datasetsByExtendedId, models));
              }}
              placeholder="Comma-separated SMILES"
              data-cy="excluded-starting-materials-input"
            />
          </div>
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Minimum number of providers"
          helperText="Minimum number of providers that must offer a starting material for it to be considered."
        >
          <div data-cy="min-providers-wrapper">
            <NumericInput
              large
              inputClassName={numericInputStyle()}
              min={MIN_N_PROVIDERS_RANGE[0]}
              max={MIN_N_PROVIDERS_RANGE[1]}
              majorStepSize={1}
              minorStepSize={1}
              disabled={disabled}
              value={searchParams.min_n_providers}
              onValueChange={value => {
                const clampedValue = clamp(
                  value,
                  MIN_N_PROVIDERS_RANGE[0],
                  MIN_N_PROVIDERS_RANGE[1]
                );
                onScalarPropertyChange('min_n_providers')(clampedValue);
              }}
              data-cy="min-providers-input"
            />
          </div>
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Maximum number of atoms"
          helperText="Maximum number of atoms of a compound to be considered a starting material."
        >
          <div data-cy="max-atoms-wrapper">
            <NumericInput
              large
              inputClassName={numericInputStyle()}
              min={0}
              majorStepSize={1}
              minorStepSize={1}
              disabled={disabled}
              value={searchParams.max_n_atoms}
              onValueChange={value => {
                const clampedValue = Math.max(0, value);
                onScalarPropertyChange('max_n_atoms')(clampedValue);
              }}
              data-cy="max-atoms-input"
            />
          </div>
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Maximum price"
          helperText="Maximum price of a starting material."
        >
          <div data-cy="max-price-wrapper">
            <NumericInput
              large
              inputClassName={numericInputStyleWithIcon()}
              min={0}
              majorStepSize={1}
              minorStepSize={1}
              disabled={disabled}
              leftIcon="dollar"
              value={searchParams.max_price.toLocaleString('en-US')}
              onValueChange={value => {
                const clampedValue = Math.max(0, value);
                onScalarPropertyChange('max_price')(clampedValue);
              }}
              locale="en-US"
              data-cy="max-price-input"
            />
          </div>
        </ParamContainer>

        <ParamContainer
          contentClassName={paramContainerInnerStyle()}
          disabled={disabled}
          label="Maximum price per gram"
          helperText="Maximum price of a starting material per gram."
        >
          <div data-cy="max-price-per-gram-wrapper">
            <NumericInput
              large
              inputClassName={numericInputStyleWithIcon()}
              min={0}
              majorStepSize={1}
              minorStepSize={1}
              disabled={disabled}
              leftIcon="dollar"
              value={searchParams.max_price_per_gram.toLocaleString('en-US')}
              onValueChange={value => {
                const clampedValue = Math.max(0, value);
                onScalarPropertyChange('max_price_per_gram')(clampedValue);
              }}
              locale="en-US"
              data-cy="max-price-input"
            />
          </div>
        </ParamContainer>
      </ParamGroup>

      {after}

      {footer && <Card className={cx(cls.footer, footerClassName)}>{footer}</Card>}
    </Root>
  );
}
